import {Component, OnInit, OnDestroy, Input, Optional} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {Subscription} from 'rxjs';
import {FormBuilder, Validators, FormGroup, FormArray, AbstractControl, ValidationErrors} from '@angular/forms';
import {TranslationService, TranslationEventService, Translatable, DateFormatService} from '@ngmedax/translation';
import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {LayoutService} from '@ngmedax/layout';
import {SimuService} from '@ngmedax/simu';
import {LoginService} from '@ngmedax/login';
import {WorkflowService} from '../services/workflow.service';
import {QuestionnairePathHashService} from '../services/questionnaire/path-hash.service';
import {Workflow, Questionnaire} from '@ngmedax/common-questionnaire-types';
import {TRANSLATION_CRUD_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';

export interface WorkflowCrudComponent extends Translatable {}

function atLeastOneQuestionnaire(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (Array.isArray(value) && value.length > 0) {
    return null;
  }
  return {noQuestionnaires: true};
}

@Component({
  selector: 'app-workflow-crud',
  templateUrl: './workflow-crud.component.html',
  styleUrls: ['./workflow-crud.component.css']
})
@Translatable({ scope: TRANSLATION_CRUD_SCOPE, keys: KEYS })
export class WorkflowCrudComponent implements OnInit, OnDestroy {
  @Input() public workflowId: string | null = null;

  public workflowForm: FormGroup;
  public dirty = true;
  public loading = false;
  public showCloseWinBtn = false;
  private subscriptions: Subscription[] = [];
  public locale = 'de_DE';
  public collapsedStages: boolean[] = [];
  public activeTab: string = 'steps';
  public conditionsVisibleStages: number[] = [];
  public stageToAdd: number | '' = '';

  public allQuestionnaires: Questionnaire[] = [];
  public usedQuestionnaireIds: string[] = [];
  public filteredQuestionnaires: Questionnaire[] = [];
  public questionnaireIdToVariables: { [questionnaireId: string]: string[] } = {};

  public newVariable = {
    name: '',
    type: 'questionnaire' as 'questionnaire' | 'survey',
    questionnaireId: '',
    variable: ''
  };

  private variableTypeMappings: { [key: string]: 'numeric' | 'date' | 'boolean' | 'text' } = {
    'client.age': 'numeric',
    'client.ageInMonths': 'numeric',
    'client.birthDate': 'date',
    'client.isMale': 'boolean',
    'client.isFemale': 'boolean',
    'client.isDiverse': 'boolean',
    'client.caseNr': 'numeric',
    'client.customerNr': 'numeric',
    'client.zipCode': 'numeric',
    'client.city': 'text',
    'client.street': 'text',
    'client.streetNr': 'numeric',
    'client.location': 'text',
    'client.status': 'text',
    'client.lastName': 'text',
    'client.firstName': 'text',
    'client.anonymous': 'boolean',
    'client.pseudonymous': 'boolean'
  };

  public get surveyVariables(): string[] {
    return Object.keys(this.variableTypeMappings);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private workflowService: WorkflowService,
    private configService: ConfigService,
    private registryService: RegistryService,
    private layoutService: LayoutService,
    private simuService: SimuService,
    private loginService: LoginService,
    private location: Location,
    private translationEvents: TranslationEventService,
    private translationService: TranslationService,
    private pathHashService: QuestionnairePathHashService,
    private dateFormatService: DateFormatService,
  ) {}

  public ngOnInit(): void {
    this.translationService && (this.locale = this.translationService.getLocale());

    this.workflowForm = this.formBuilder.group({
      _id: [null],
      name: this.formBuilder.group({
        de_DE: ['', Validators.required]
      }),
      description: this.formBuilder.group({
        de_DE: ['']
      }),
      stages: this.formBuilder.array([]),
      variables: this.formBuilder.array([])
    });

    this.activatedRoute.queryParams.subscribe((params: any) => params.closeWinBtn && (this.showCloseWinBtn = true));
    const routeSub = this.activatedRoute.params.subscribe(async (params: any) => {
      if (params.id) {
        this.workflowId = params.id;
        await this.loadWorkflow(params.id);
      } else if (this.workflowId) {
        await this.loadWorkflow(this.workflowId);
      } else {
        this.dirty = false;
      }
    });
    this.subscriptions.push(routeSub);

    this.workflowService.loadQuestionnaires()
      .then(qs => {
        this.allQuestionnaires = Array.isArray(qs) ? qs : [];
        this.updateFilteredQuestionnaires();
      })
      .catch(() => {});
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  get stages(): FormArray {
    return this.workflowForm.get('stages') as FormArray;
  }

  get variables(): FormArray {
    return this.workflowForm.get('variables') as FormArray;
  }

  //

  private async loadWorkflow(workflowId: string) {
    try {
      this.loading = true;
      this.layoutService.showPreloader();
      const w = await this.workflowService.loadWorkflow(workflowId);

      if (w && this.simuService.isSimuMode()) {
        const user = await this.loginService.getUser();
        const userName = user.getUsername();
        const createdBy = w.createdBy;
        if (userName && createdBy && userName !== createdBy) {
          this.location.back();
        }
      }

      if (!w) {
        alert(this._(KEYS.CRUD.FOUND_NO_ENTRY_BY_ID) + ' ' + workflowId);
        return;
      }
      this.renderWorkflow(w);
      this.dirty = false;
      this.layoutService.hidePreloader();
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.layoutService.hidePreloader();
      alert(this._(KEYS.CRUD.ERROR_LOADING_WORKFLOW));
      this.loading = false;
    }
  }

  private renderWorkflow(workflow: Workflow) {
    this.workflowForm.reset();
    this.stages.clear();
    (this.workflowForm.get('variables') as FormArray).clear();
    this.collapsedStages = [];

    this.workflowForm.get('_id')!.setValue(workflow._id || workflow.id || null);

    if (workflow.name) {
      for (const locale of Object.keys(workflow.name)) {
        const ctrl = this.workflowForm.get('name.' + locale);
        ctrl && ctrl.setValue(workflow.name[locale]);
      }
    }

    if (workflow.description) {
      for (const locale of Object.keys(workflow.description)) {
        const ctrl = this.workflowForm.get('description.' + locale);
        ctrl && ctrl.setValue(workflow.description[locale]);
      }
    }

    if (workflow.stages && workflow.stages.length) {
      workflow.stages.forEach((stage, index) => {
        const stageGroup = this.createStageFormGroup();
        if (stage.name) {
          for (const locale of Object.keys(stage.name)) {
            const ctrl = stageGroup.get('name.' + locale);
            ctrl && ctrl.setValue(stage.name[locale]);
          }
        }
        if (stage.description) {
          for (const locale of Object.keys(stage.description)) {
            const ctrl = stageGroup.get('description.' + locale);
            ctrl && ctrl.setValue(stage.description[locale]);
          }
        }

        stageGroup.get('autoStart')!.setValue(stage.autoStart || false);
        stageGroup.get('skipSigning')!.setValue(stage.skipSigning || false);
        stageGroup.get('generateExports')!.setValue(stage.generateExports || false);
        stageGroup.get('generateFormPDFs')!.setValue(stage.generateFormPDFs || false);
        stageGroup.get('prefill')!.setValue(stage.prefill || false);
        stageGroup.get('questionnaires')!.setValue(stage.questionnaires || []);
        stageGroup.get('expression')!.setValue(stage.expression || []);

        this.stages.push(stageGroup);
        this.collapsedStages.push(true);
      });
        // keep user choices; constraints will be warned on save only
    }

    const vars: any[] = (workflow as any).variables || [];
    if (Array.isArray(vars)) {
      for (const v of vars) {
        this.variables.push(this.createVariableFormGroup(v));
      }
    }

    this.updateFilteredQuestionnaires();

    // initialize visible conditions stages to those that already have expressions
    this.conditionsVisibleStages = [];
    if (Array.isArray(workflow.stages)) {
      workflow.stages.forEach((s: any, idx: number) => {
        if (s && typeof s.expression === 'string' && s.expression.trim().length > 0) {
          this.conditionsVisibleStages.push(idx);
        }
      });
    }
  }

  private createStageFormGroup(): FormGroup {
    return this.formBuilder.group({
      name: this.formBuilder.group({
        de_DE: ['']
      }),
      description: this.formBuilder.group({
        de_DE: ['']
      }),
      expression: [''],
      autoStart: [false],
      skipSigning: [false],
      generateExports: [false],
      generateFormPDFs: [false],
      prefill: [false],
      questionnaires: [[], atLeastOneQuestionnaire]
    });
  }

  public onAddStage() {
    this.stages.push(this.createStageFormGroup());
    this.collapsedStages.push(false);
    this.dirty = true;
    this.updateFilteredQuestionnaires();
  }

  public async onRemoveStage(index: number) {
    if (await confirm(this._(KEYS.CRUD.CONFIRM_DELETE_STAGE))) {
      this.stages.removeAt(index);
      this.collapsedStages.splice(index, 1);
      // adjust visible conditions list and clear expression for removed stage
      this.conditionsVisibleStages = this.conditionsVisibleStages
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      this.dirty = true;
      this.updateFilteredQuestionnaires();
    }
  }

  public async onSubmit() {
    await this.saveWorkflow();
  }

  private async saveWorkflow() {
    try {
      const workflow: Workflow = this.workflowForm.value;
      const lastStage = this.stages.length > 0 ? (this.stages.at(this.stages.length - 1) as FormGroup) : null;
      if (lastStage) {
        const skipSigning = !!lastStage.get('skipSigning')?.value;
        const generateExports = !!lastStage.get('generateExports')?.value;
        const generateFormPDFs = !!lastStage.get('generateFormPDFs')?.value;
        if (skipSigning || !generateExports || !generateFormPDFs) {
          // alert(this._(KEYS.CRUD.WARNING_LAST_STAGE_CONSTRAINTS));
        }
      }
      let savedWorkflow: Workflow;

      if (workflow._id) {
        savedWorkflow = await this.workflowService.updateWorkflow(workflow._id, workflow);
      } else {
        savedWorkflow = await this.workflowService.createWorkflow(workflow);
      }

      this.dirty = false;
      this.renderWorkflow(savedWorkflow);
      alert(this._(KEYS.CRUD.SUCCESSFULLY_SAVED_WORKFLOW));
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.CRUD.ERROR_SAVING_WORKFLOW));
    }
  }

  public onQuestionnairesSelect(questionnaires: string[], stageIndex: number) {
    const stage = this.stages.at(stageIndex) as FormGroup;
    stage.get('questionnaires')!.setValue(questionnaires);
    this.dirty = true;
    this.updateFilteredQuestionnaires();
  }

  public toggleStage(index: number) {
    this.collapsedStages[index] = !this.collapsedStages[index];
  }

  public getStageDisplayName(index: number): string {
    const nameCtrl = this.stages.at(index).get('name.de_DE');
    const val = (nameCtrl && typeof (nameCtrl as any).value === 'string') ? (nameCtrl as any).value as string : '';
    const name = val && val.trim() ? val.trim() : null;
    if (name) {
      return name;
    }
    return this._(KEYS.CRUD.STAGE) + ' ' + (index + 1);
  }

  public addConditionsStage(): void {
    if (this.stageToAdd === '' || typeof this.stageToAdd !== 'number') { return; }
    const idx = this.stageToAdd as number;
    if (!this.conditionsVisibleStages.includes(idx)) {
      this.conditionsVisibleStages.push(idx);
    }
    this.stageToAdd = '';
  }

  public removeConditionsStage(idx: number): void {
    this.conditionsVisibleStages = this.conditionsVisibleStages.filter(i => i !== idx);
    // clear compiled expression on the transient workflow model if present
    const w: any = this.workflowForm.value;
    if (w && Array.isArray(w.stages) && w.stages[idx]) {
      delete w.stages[idx].expression;
    }
  }

  public _!: (text: string, variables?: any) => string;
  public getDateFormat!: (format: string, locale?: string) => string;
  public readonly KEYS = KEYS;

  /**
   * Creates a form group representing a workflow variable mapping.
   */
  private createVariableFormGroup(initial?: any): FormGroup {
    return this.formBuilder.group({
      name: [initial?.name || '', [Validators.required]],
      target: this.formBuilder.group({
        type: [initial?.target?.type || 'questionnaire', [Validators.required]],
        id: [initial?.target?.id || ''],
        variable: [initial?.target?.variable || '', [Validators.required]],
        options: [initial?.target?.options || null]
      })
    });
  }

  /**
   * Updates the filtered questionnaires list based on questionnaires used in stages.
   */
  private updateFilteredQuestionnaires(): void {
    const ids: string[] = [];
    for (let i = 0; i < this.stages.length; i++) {
      const q = (this.stages.at(i) as FormGroup).get('questionnaires')!.value as string[];
      if (Array.isArray(q)) {
        for (const id of q) {
          if (id && !ids.includes(id)) {
            ids.push(id);
          }
        }
      }
    }
    this.usedQuestionnaireIds = ids;
    this.filteredQuestionnaires = this.allQuestionnaires.filter(q => ids.includes(q.id));

    if (this.newVariable.type === 'questionnaire' && !this.usedQuestionnaireIds.includes(this.newVariable.questionnaireId)) {
      this.newVariable.questionnaireId = '';
      this.newVariable.variable = '';
    }

    this.buildQuestionnaireVariableIndex();
  }

  /**
   * Builds an index of available variable keys per questionnaire id for allowed scopes.
   */
  private buildQuestionnaireVariableIndex(): void {
    const scopes = ['inline', 'formula', 'scoring'];
    const index: { [id: string]: string[] } = {};
    for (const q of this.filteredQuestionnaires) {
      const variables = (((q as any).meta || {}).options || {}).variables || {};
      const list: string[] = [];
      for (const scope of scopes) {
        if (variables[scope] && typeof variables[scope] === 'object') {
          for (const varName of Object.keys(variables[scope])) {
            list.push(`${scope}.${varName}`);
          }
        }
      }
      index[q.id] = list.sort();
    }
    this.questionnaireIdToVariables = index;
  }

  /**
   * Adds a new variable mapping if valid and unique.
   */
  public async onAddVariable(): Promise<void> {
    const name = (this.newVariable.name || '').trim();
    const type = this.newVariable.type;
    const questionnaireId = this.newVariable.questionnaireId;
    const variable = this.newVariable.variable;

    // validate js identifier: start with letter/_/$, then letters/digits/_/$
    const isValidJsIdentifier = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name);
    if (!name || !isValidJsIdentifier || !type) {
      return;
    }
    if (type === 'questionnaire' && (!questionnaireId || !variable)) {
      return;
    }
    if (type === 'survey' && !variable) {
      return;
    }

    const exists = this.variables.controls.some(ctrl => (ctrl.get('name')!.value || '').trim().toLowerCase() === name.toLowerCase());
    if (exists) {
      return;
    }

    let enriched: any;
    if (type === 'questionnaire') {
      enriched = await this.enrichVariableWithFormat({
        name,
        target: { type, id: questionnaireId, variable }
      });
    } else {
      const fmt = this.variableTypeMappings[variable];
      const options: any = fmt ? { format: fmt } : null;
      enriched = {
        name,
        target: {
          type: 'survey',
          id: null,
          variable,
          options
        }
      };
    }

    this.variables.push(this.createVariableFormGroup(enriched));

    this.newVariable = { name: '', type: this.newVariable.type, questionnaireId: '', variable: '' };
    this.dirty = true;
  }

  /**
   * Removes a variable mapping by index.
   */
  public onRemoveVariable(index: number): void {
    this.variables.removeAt(index);
    this.dirty = true;
  }

  /**
   * Returns questionnaire title for the current locale by id.
   */
  public getQuestionnaireTitle(id: string): string {
    let q = this.allQuestionnaires.find(x => x.id === id);
    if (!q) {
      q = this.filteredQuestionnaires.find(x => x.id === id) as any;
    }
    const titles = (q && (q as any).meta && (q as any).meta.title) || {};
    const deTitle = titles['de_DE'] || (Object.values(titles)[0] as string) || '';
    return deTitle || id || '';
  }

  /**
   * Enriches a workflow variable with format and date options from questionnaire by pathHash.
   */
  private async enrichVariableWithFormat(v: any): Promise<any> {
    try {
      const q = await this.workflowService.loadQuestionnaire(v.target.id);
      if (!q) {
        return v;
      }
      const variables = ((((q as any).meta || {}).options || {}).variables || {});
      const [scope, varName] = (v.target.variable || '').split('.', 2);
      const scopeMap = variables[scope] || {};
      const pathHash = scopeMap[varName] ? Object.keys(scopeMap[varName])[0] : null;
      if (!pathHash) {
        return v;
      }
      const map = this.pathHashService.addPathHashes(q as any);
      const container = map[pathHash];
      if (!container) {
        return v;
      }

      const nearestWithType = this.findNearestContainer(map, pathHash, (c: any) => !!c.format || !!c.dataType) || container;
      const format = (nearestWithType as any).format || null;
      const dataType = (nearestWithType as any).dataType || null;

      const opts = (nearestWithType as any).options || {};
      const targetOptions: any = {};
      if (dataType) { targetOptions.dataType = dataType; }
      if (format) { targetOptions.format = format; }
      if (format === 'date') {
        if (opts.fullDate) { targetOptions.fullDate = true; }
        else if (opts.fullDateTime) { targetOptions.fullDateTime = true; }
        else if (opts.monthDate) { targetOptions.monthDate = true; }
        else if (opts.yearDate) { targetOptions.yearDate = true; }
      }
      v.target.options = Object.keys(targetOptions).length ? targetOptions : null;
      return v;
    } catch (e) {
      return v;
    }
  }

  /**
   * Returns the nearest ancestor (including start) that matches predicate; walks parentHash chain.
   */
  private findNearestContainer(map: { [hash: string]: any }, startHash: string, predicate: (c: any) => boolean): any {
    const visited: { [hash: string]: boolean } = {};
    let current = startHash;
    while (current && !visited[current]) {
      visited[current] = true;
      const c = map[current];
      if (!c) { break; }
      if (predicate(c)) { return c; }
      current = (c as any).parentHash;
    }
    return null;
  }
}
